import type { ChatMessage } from '@/stores/chat-store'
import type { MessageJobContext, MessageMatchData, QuickAction, RevisionReadyEvent } from '@/types'

export type InteractiveMetadataField = 'quick_actions' | 'quick_replies'

const DISMISSED_INTERACTIVE_STORAGE_PREFIX = 'pelgo:chat:dismissed-interactive:'

export const DEFAULT_SUPPORT_QUICK_ACTIONS: QuickAction[] = [
  { label: 'Connect with support', action: 'support_request', target: '' },
  { label: 'Continue chatting', action: 'message', target: 'Continue chatting' },
]

const HUMAN_COACH_USER_MESSAGE_PATTERN =
  /human coach|help of a human coach|help of human coach|reach out to us/i

const SUPPORT_ESCALATION_REPLY_PATTERN =
  /escalat|pelgo team|human member|reach out to you directly|flagging this conversation|flagging your request/i

export function getDismissedInteractiveStorageKey(chatId: string): string {
  return `${DISMISSED_INTERACTIVE_STORAGE_PREFIX}${chatId}`
}

export function loadDismissedInteractiveMessageIds(chatId: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(getDismissedInteractiveStorageKey(chatId))
    if (!raw) return new Set()

    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()

    return new Set(parsed.filter((id): id is string => typeof id === 'string'))
  } catch {
    return new Set()
  }
}

export function persistDismissedInteractiveMessageId(
  chatId: string,
  messageId: string | number,
): void {
  const ids = loadDismissedInteractiveMessageIds(chatId)
  ids.add(String(messageId))
  sessionStorage.setItem(getDismissedInteractiveStorageKey(chatId), JSON.stringify([...ids]))
}

export function clearMessageInteractiveMetadata(
  message: ChatMessage,
  fields: InteractiveMetadataField[],
): ChatMessage {
  if (!message.msg_metadata) return message

  const nextMetadata = { ...message.msg_metadata }
  let changed = false

  for (const field of fields) {
    const value = nextMetadata[field]
    if (Array.isArray(value) && value.length > 0) {
      nextMetadata[field] = []
      changed = true
    }
  }

  return changed ? { ...message, msg_metadata: nextMetadata } : message
}

export function clearMessagesInteractiveMetadata(
  messages: ChatMessage[],
  messageId: string | number,
  fields: InteractiveMetadataField[],
): ChatMessage[] {
  const targetId = String(messageId)
  return messages.map((message) =>
    String(message.id) === targetId ? clearMessageInteractiveMetadata(message, fields) : message,
  )
}

export function stripDismissedInteractiveMetadata(
  messages: ChatMessage[],
  dismissedIds: Set<string>,
): ChatMessage[] {
  if (dismissedIds.size === 0) return messages

  return messages.map((message) =>
    dismissedIds.has(String(message.id))
      ? clearMessageInteractiveMetadata(message, ['quick_actions', 'quick_replies'])
      : message,
  )
}

export function getVisibleQuickActions(
  message: ChatMessage,
  dismissedIds: Set<string>,
): QuickAction[] {
  if (dismissedIds.has(String(message.id))) return []
  return message.msg_metadata?.quick_actions ?? []
}

export function getVisibleQuickReplies(message: ChatMessage, dismissedIds: Set<string>): string[] {
  if (dismissedIds.has(String(message.id))) return []
  return message.msg_metadata?.quick_replies ?? []
}

export function isKnownQuickAction(action: QuickAction): boolean {
  return (
    action.action === 'navigate' ||
    action.action === 'message' ||
    action.action === 'support_request'
  )
}

function parseMessageTime(value: string | Date | undefined): number {
  const timestamp = new Date(value ?? '').getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

function hasInteractiveField(message: ChatMessage, field: InteractiveMetadataField): boolean {
  const value = message.msg_metadata?.[field]
  return Array.isArray(value) && value.length > 0
}

export function findLatestAssistantMessageId(messages: ChatMessage[]): string | null {
  let latestId: string | null = null
  let latestTime = -1
  let latestIndex = -1

  messages.forEach((message, index) => {
    if (message.role !== 'assistant') return

    const time = parseMessageTime(message.created_at)
    if (time > latestTime || (time === latestTime && index > latestIndex)) {
      latestTime = time
      latestIndex = index
      latestId = String(message.id)
    }
  })

  return latestId
}

export function retainOnlyLatestInteractiveMetadata(
  messages: ChatMessage[],
  field: InteractiveMetadataField,
): { messages: ChatMessage[]; supersededIds: string[] } {
  const latestAssistantId = findLatestAssistantMessageId(messages)
  if (!latestAssistantId) return { messages, supersededIds: [] }

  const supersededIds: string[] = []
  const next = messages.map((message) => {
    if (message.role !== 'assistant') return message
    if (String(message.id) === latestAssistantId || !hasInteractiveField(message, field)) {
      return message
    }

    supersededIds.push(String(message.id))
    return clearMessageInteractiveMetadata(message, [field])
  })

  return { messages: next, supersededIds }
}

export function retainOnlyLatestInteractiveMetadataForAllFields(messages: ChatMessage[]): {
  messages: ChatMessage[]
  supersededIds: string[]
} {
  let result = messages
  const supersededIds = new Set<string>()

  for (const field of ['quick_actions', 'quick_replies'] as const) {
    const retained = retainOnlyLatestInteractiveMetadata(result, field)
    result = retained.messages
    for (const id of retained.supersededIds) {
      supersededIds.add(id)
    }
  }

  return { messages: result, supersededIds: [...supersededIds] }
}

export function resolveStreamingTargetMessageId(
  messages: ChatMessage[],
  targetMessageId: string | number | undefined,
  streamingMessageId: string | number | undefined,
): string | null {
  const messageIds = new Set(messages.map((message) => String(message.id)))

  if (targetMessageId !== undefined && targetMessageId !== null) {
    const normalizedTarget = String(targetMessageId)
    if (messageIds.has(normalizedTarget)) {
      return normalizedTarget
    }
  }

  if (streamingMessageId !== undefined) {
    const normalizedStreaming = String(streamingMessageId)
    if (messageIds.has(normalizedStreaming)) {
      return normalizedStreaming
    }
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (message?.role === 'assistant') {
      return String(message.id)
    }
  }

  return null
}

export function applyInteractiveMetadataUpdate(
  messages: ChatMessage[],
  targetMessageId: string | number,
  field: InteractiveMetadataField,
  metadataPatch: Partial<NonNullable<ChatMessage['msg_metadata']>>,
): { messages: ChatMessage[]; supersededIds: string[] } {
  const targetId = String(targetMessageId)
  const targetExists = messages.some((message) => String(message.id) === targetId)
  if (!targetExists) {
    return { messages, supersededIds: [] }
  }

  const supersededIds: string[] = []

  const next = messages.map((message) => {
    const messageId = String(message.id)

    if (messageId === targetId) {
      return {
        ...message,
        msg_metadata: {
          ...(message.msg_metadata ?? {}),
          ...metadataPatch,
        },
      }
    }

    if (!hasInteractiveField(message, field)) return message

    supersededIds.push(messageId)
    return clearMessageInteractiveMetadata(message, [field])
  })

  return { messages: next, supersededIds }
}

export function registerDismissedInteractiveMessageIds(
  chatId: string | null | undefined,
  messageIds: Iterable<string | number>,
  dismissedIds: Set<string>,
): void {
  for (const messageId of messageIds) {
    const normalizedId = String(messageId)
    dismissedIds.add(normalizedId)
    if (chatId) {
      persistDismissedInteractiveMessageId(chatId, normalizedId)
    }
  }
}

export function findPreviousUserMessage(
  messages: ChatMessage[],
  assistantMessageId: string | number,
): ChatMessage | undefined {
  const assistantIndex = messages.findIndex(
    (message) => String(message.id) === String(assistantMessageId),
  )
  if (assistantIndex <= 0) return undefined

  for (let index = assistantIndex - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (message?.role === 'user') return message
  }

  return undefined
}

export function shouldAttachSupportQuickActions(
  userMessage: string | undefined,
  assistantMessage: string,
  hasQuickActions: boolean,
): boolean {
  if (hasQuickActions) return false

  const userText = userMessage?.trim() ?? ''
  const assistantText = assistantMessage.trim()
  if (!userText && !assistantText) return false

  if (HUMAN_COACH_USER_MESSAGE_PATTERN.test(userText)) return true
  if (SUPPORT_ESCALATION_REPLY_PATTERN.test(assistantText)) return true

  return false
}

export function attachSupportQuickActionsWhenMissing(messages: ChatMessage[]): ChatMessage[] {
  return messages.map((message) => {
    if (message.role !== 'assistant' || hasInteractiveField(message, 'quick_actions')) {
      return message
    }

    const previousUser = findPreviousUserMessage(messages, message.id)
    if (!shouldAttachSupportQuickActions(previousUser?.content, message.content, false)) {
      return message
    }

    return {
      ...message,
      msg_metadata: {
        ...(message.msg_metadata ?? {}),
        quick_actions: DEFAULT_SUPPORT_QUICK_ACTIONS,
      },
    }
  })
}

// Helper function to format timestamps with date and time
export function formatMessageTime(dateString: string | Date): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  if (date.toDateString() === today.toDateString()) {
    return timeStr
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday, ${timeStr}`
  }
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${dateStr}, ${timeStr}`
}

// Helper function to get date label (Today, Yesterday, or date)
export function getDateLabel(dateString: string | Date): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday'
  }
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

// Group messages by date
export function groupMessagesByDate(messages: ChatMessage[]) {
  const groups: { [key: string]: ChatMessage[] } = {}

  for (const msg of messages) {
    const dateLabel = getDateLabel(msg.created_at || new Date())
    if (!groups[dateLabel]) {
      groups[dateLabel] = []
    }
    groups[dateLabel].push(msg)
  }

  return groups
}

// Filter out hidden data from user messages for display
export const filterHiddenData = (content: string) =>
  content
    .replace(/\s*<!--MATCH_CONTEXT:.*?-->/gs, '')
    .replace(/\s*<!--MATCH_DATA:[\s\S]*?(-->|$)/g, '')
    .replace(/\s*\[system:match_data\][\s\S]*?(\[\/system\]|$)/g, '')
    .replace(/\s*\[system:job_context\][\s\S]*?(\[\/system\]|$)/g, '')
    .replace(/\s*\[system:revision_data\][\s\S]*?(\[\/system\]|$)/g, '')
    .trim()

// Strip AI intent markers from displayed content
export const stripIntentMarkers = (content: string) =>
  content
    .replace(/\n?\[GENERATE_RESUME\]\s*/g, '')
    .replace(/\n?\[RESUME_PREVIEW\]\s*/g, '')
    .replace(/\n?\[SUPPORT_REQUEST\]\s*/g, '')
    .replace(/\n?\[FOCUS:[^\]]+\]\s*/g, '')
    .replace(/\n?\[RESUME_CONTENT_UPDATED\]\s*/g, '')
    .replace(/\n?\[CONTENT:\{[\s\S]*\}\]\s*/g, '')
    .replace(/\n?\[LEARN_MODULE\]\s*/g, '')
    .replace(/\n?\[GENERATE_LEARNING:\{[^}]*\}\]\s*/g, '')
    .replace(/\n?\[MODULE:\{[^}]*\}\]\s*/g, '')
    .replace(/\n?\[LEARN_PROGRESS:\{[^}]*\}\]\s*/g, '')
    .trim()

// Parse match data embedded in message content
export const parseMatchData = (content: string): MessageMatchData | undefined => {
  const match =
    content.match(/\[system:match_data\]([\s\S]*?)\[\/system\]/) ||
    content.match(/<!--MATCH_DATA:([\s\S]*?)-->/)
  if (match?.[1]) {
    try {
      return JSON.parse(match[1]) as MessageMatchData
    } catch {
      return undefined
    }
  }
  return undefined
}

// Parse job context embedded in message content
export const parseJobContext = (content: string): MessageJobContext | undefined => {
  const match = content.match(/\[system:job_context\]([\s\S]*?)\[\/system\]/)
  if (match?.[1]) {
    try {
      return JSON.parse(match[1]) as MessageJobContext
    } catch {
      return undefined
    }
  }
  return undefined
}

// Parse revision data embedded in message content
export const parseRevisionData = (content: string): RevisionReadyEvent | undefined => {
  const match = content.match(/\[system:revision_data\]([\s\S]*?)\[\/system\]/)
  if (match?.[1]) {
    try {
      return JSON.parse(match[1]) as RevisionReadyEvent
    } catch {
      return undefined
    }
  }
  return undefined
}

// Determine how to render a message based on its metadata and state
export type MessageDisplayType = 'streaming' | 'resume_upload' | 'error' | 'assistant' | 'user'

export function getMessageDisplayType(msg: ChatMessage): MessageDisplayType {
  if (msg.isStreaming && !msg.content) return 'streaming'
  if (msg.error && msg.role === 'assistant') return 'error'
  if (msg.msg_metadata?.type === 'resume_upload') return 'resume_upload'
  return msg.role
}

// URL detection helpers
export const isUrl = (text: string) => /^https?:\/\//i.test(text.trim())

// Domains that always require authentication
const PROTECTED_DOMAINS = ['linkedin.com']

export const isProtectedUrl = (url: string) =>
  PROTECTED_DOMAINS.some((domain) => url.toLowerCase().includes(domain))
